import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Result,
  Descriptions,
  Tag,
  Spin,
  Button,
  Space,
  Typography,
} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ClockCircleFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { api } from '../services/api';
import { TX_STATUS, type TransactionRecord } from '../types';

const { Title, Text } = Typography;

interface StatusUI {
  label: string;
  color: string;
  icon: React.ReactNode;
  resultStatus: 'success' | 'error' | 'warning' | 'info';
}

const STATUS_UI: Record<number, StatusUI> = {
  [TX_STATUS.CHECKOUT_APPROVED]: {
    label: 'APROBADO',
    color: '#28a745',
    icon: <CheckCircleFilled style={{ fontSize: 64, color: '#28a745' }} />,
    resultStatus: 'success',
  },
  [TX_STATUS.CHECKOUT_DECLINED]: {
    label: 'RECHAZADO',
    color: '#dc3545',
    icon: <CloseCircleFilled style={{ fontSize: 64, color: '#dc3545' }} />,
    resultStatus: 'error',
  },
  [TX_STATUS.CHECKOUT_ERROR]: {
    label: 'ERROR',
    color: '#dc3545',
    icon: <CloseCircleFilled style={{ fontSize: 64, color: '#dc3545' }} />,
    resultStatus: 'error',
  },
  [TX_STATUS.CHECKOUT_PENDING]: {
    label: 'PENDIENTE',
    color: '#ffc107',
    icon: <ClockCircleFilled style={{ fontSize: 64, color: '#ffc107' }} />,
    resultStatus: 'warning',
  },
};

const DEFAULT_STATUS_UI: StatusUI = {
  label: 'DESCONOCIDO',
  color: '#999',
  icon: <ExclamationCircleFilled style={{ fontSize: 64, color: '#999' }} />,
  resultStatus: 'info',
};

function formatCurrency(amount: number, currency: string = 'CRC'): string {
  const symbol = currency === 'USD' ? '$' : '₡';
  return `${symbol}${amount.toLocaleString('es-CR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function processorLabel(processor: string): string {
  const map: Record<string, string> = {
    CofasaCGP: 'Transferencia BAC',
    BAC: 'Transferencia BAC',
    PowerTranz: 'Tarjeta',
    Sinpe: 'SINPE Móvil',
  };
  return map[processor] || processor;
}

export function TransactionStatus() {
  const { orderId, processor } = useParams<{ orderId: string; processor: string }>();
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState<TransactionRecord | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;

    const fetchTx = async () => {
      setLoading(true);
      try {
        const response = await api.getTransactionByOrderId(orderId);
        if (cancelled) return;

        if (response.success && response.body && response.body.length > 0) {
          // Si hay múltiples transactions con el mismo orderId, tomamos la más reciente
          const sorted = [...response.body].sort((a, b) => b.createdAt - a.createdAt);
          setTransaction(sorted[0]);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[TransactionStatus] error fetching transaction:', err);
        setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTx();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <Spin size="large" tip="Cargando estado del pago..." />
      </div>
    );
  }

  if (notFound || !transaction) {
    return (
      <Result
        status="404"
        title="Transacción no encontrada"
        subTitle={`No encontramos una transacción con orden ${orderId}.`}
      />
    );
  }

  const ui = STATUS_UI[transaction.status] || DEFAULT_STATUS_UI;
  const currency =
    transaction.paymentMethod?.currency || transaction.paymentMethod?.cod_moneda || 'CRC';
  const customerName =
    transaction.paymentMethod?.name ||
    transaction.paymentMethod?.nombre ||
    transaction.personalInfo?.name ||
    'Cliente';
  const numeroCliente =
    transaction.paymentMethod?.numero_cliente ||
    transaction.metadata?.numero_cliente ||
    '';
  const identification =
    transaction.personalInfo?.identification ||
    transaction.paymentMethod?.identification ||
    '';
  const txDateLocal = dayjs.unix(transaction.createdAt).format('DD/MM/YYYY HH:mm:ss');

  return (
    <div
      style={{
        maxWidth: 720,
        margin: '24px auto',
        padding: '0 16px',
      }}
    >
      <Card>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          {ui.icon}
          <Title level={2} style={{ color: ui.color, margin: '8px 0' }}>
            Pago {ui.label}
          </Title>
          <Text type="secondary">{processorLabel(processor || transaction.processor)}</Text>
        </div>

        <Descriptions
          column={1}
          bordered
          size="small"
          labelStyle={{ width: 180, fontWeight: 600 }}
        >
          <Descriptions.Item label="Estado">
            <Tag color={ui.color}>{ui.label}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Monto">
            <Text strong>{formatCurrency(transaction.amount, currency)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Referencia">
            {transaction.reference || transaction.authorization || '—'}
          </Descriptions.Item>
          {transaction.authorization && transaction.reference !== transaction.authorization && (
            <Descriptions.Item label="Autorización">
              {transaction.authorization}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Cliente">{customerName}</Descriptions.Item>
          {identification && (
            <Descriptions.Item label="Identificación">{identification}</Descriptions.Item>
          )}
          {numeroCliente && (
            <Descriptions.Item label="N° Cliente">{numeroCliente}</Descriptions.Item>
          )}
          <Descriptions.Item label="Fecha">{txDateLocal}</Descriptions.Item>
          <Descriptions.Item label="Método de pago">
            {processorLabel(processor || transaction.processor)}
          </Descriptions.Item>
          <Descriptions.Item label="ID de orden">{transaction.orderId}</Descriptions.Item>
        </Descriptions>

        <Space
          direction="vertical"
          size="small"
          style={{ width: '100%', marginTop: 32 }}
        >
          <Button block size="large" onClick={() => window.print()}>
            Imprimir comprobante
          </Button>
          <Text
            type="secondary"
            style={{ display: 'block', textAlign: 'center', fontSize: 12 }}
          >
            Podés cerrar esta ventana y volver al chat de COFASA.
          </Text>
        </Space>
      </Card>
    </div>
  );
}
